import {Link} from '@flixbus/honeycomb-react';
import {Country} from '@/Pages/Countries/store/types';
import {PartnerCostsPath} from '@/Pages/PartnerCosts/constants/routePaths';
import {Partner} from '@/Pages/Partners/store/types';
import {RegionalCostsPath} from '@/Pages/RegionalCosts/constants/routePaths';
import {VehicleTypesPath} from '@/Pages/VehicleTypes/constants/routePaths';

// ---------- Vehicle Type errors ----------
export enum VehicleTypeErrorCode {
  NumberOfTiresMissing = 'numberOfTires.missing',
  LngConsumptionMissing = 'lngConsumption.missing',
  DieselConsumptionMissing = 'dieselConsumption.missing',
  AdblueConsumptionMissing = 'adblueConsumption.missing'
}

export const isVehicleTypeErrorCode = (
  errorCode: string
): errorCode is VehicleTypeErrorCode =>
  Object.values(VehicleTypeErrorCode).includes(
    errorCode as VehicleTypeErrorCode
  );

export const getVehicleTypeError = (
  errorCode: VehicleTypeErrorCode
): React.ReactNode => {
  switch (errorCode) {
    case VehicleTypeErrorCode.NumberOfTiresMissing:
      return 'Number of tires';
    case VehicleTypeErrorCode.LngConsumptionMissing:
      return 'LNG Consumption';
    case VehicleTypeErrorCode.DieselConsumptionMissing:
      return 'Diesel Consumption';
    case VehicleTypeErrorCode.AdblueConsumptionMissing:
      return 'Adblue Consumption';
  }
};

// ---------- Regional or Bus Partner errors ----------
export enum RegionalOrBusPartnerErrorCode {
  DaytimeWeekdaysWorkingHourCostMissing = 'daytimeWeekdaysWorkingHourCost.missing',
  NighttimeWeekdaysWorkingHourCostMissing = 'nighttimeWeekdaysWorkingHourCost.missing',
  DaytimeWeekendsWorkingHourCostMissing = 'daytimeWeekendsWorkingHourCost.missing',
  NighttimeWeekendsWorkingHourCostMissing = 'nighttimeWeekendsWorkingHourCost.missing',
  DieselCostMissing = 'dieselCost.missing',
  AdblueCostMissing = 'adblueCost.missing',
  LngCostMissing = 'lngCost.missing',
  TiresCostMissing = 'tiresCost.missing',
  OverheadCostMissing = 'overheadCost.missing',
  CleaningCostMissing = 'cleaningCost.missing',
  CountryBusCostEntryMissing = 'countryBusCostEntry.missing',
  FlixStandardCostMissing = 'flixStandardCost.missing',
  LeasingCostMissing = 'leasingCost.missing',
  InsuranceCostMissing = 'insuranceCost.missing',
  VehicleRegistrationCostMissing = 'vehicleRegistrationCost.missing',
  VehicleRegistrationCostNotAllowed = 'vehicleRegistrationCost.notAllowed',
  MaintenanceCostMissing = 'maintenanceCost.missing',
  MaintenanceCostConflict = 'maintenanceCost.conflict',
  ReplacementCostPerKmMissing = 'replacementCostPerKm.missing',
  ReplacementCostPerHrMissing = 'replacementCostPerHr.missing'
}

export const isRegionalOrBusPartnerErrorCode = (
  errorCode: string
): errorCode is RegionalOrBusPartnerErrorCode =>
  Object.values(RegionalOrBusPartnerErrorCode).includes(
    errorCode as RegionalOrBusPartnerErrorCode
  );

export const getRegionalOrBusPartnerLevelError = (
  errorCode: RegionalOrBusPartnerErrorCode,
  busPartner: Partner,
  busPartnerCountry: Country,
  year: number
): React.ReactNode => {
  const regionalVehicleCostsLink = getRegionalCostsVehicleCostsLink(
    busPartnerCountry.id,
    year,
    'regional'
  );
  const regionalDriverCostsLink = getRegionalCostsDriverCostsLink(
    busPartnerCountry.id,
    year,
    'regional'
  );
  const partnerVehicleCostsLink = getPartnerCostsVehicleCostsLink(
    busPartner.id,
    year,
    'bus partner'
  );
  const partnerDriverCostsLink = getPartnerCostsDriverCostsLink(
    busPartner.id,
    year,
    'bus partner'
  );

  switch (errorCode) {
    case RegionalOrBusPartnerErrorCode.DaytimeWeekdaysWorkingHourCostMissing:
      return (
        <>
          Driver base salary, daytime/weekdays ({partnerDriverCostsLink} or{' '}
          {regionalDriverCostsLink})
        </>
      );
    case RegionalOrBusPartnerErrorCode.NighttimeWeekdaysWorkingHourCostMissing:
      return (
        <>
          Driver base salary, nighttime/weekdays ({partnerDriverCostsLink} or{' '}
          {regionalDriverCostsLink})
        </>
      );
    case RegionalOrBusPartnerErrorCode.DaytimeWeekendsWorkingHourCostMissing:
      return (
        <>
          Driver base salary, daytime/weekend ({partnerDriverCostsLink} or{' '}
          {regionalDriverCostsLink})
        </>
      );
    case RegionalOrBusPartnerErrorCode.NighttimeWeekendsWorkingHourCostMissing:
      return (
        <>
          Driver base salary, nighttime/weekend ({partnerDriverCostsLink} or{' '}
          {regionalDriverCostsLink})
        </>
      );
    case RegionalOrBusPartnerErrorCode.DieselCostMissing:
      return (
        <>
          Diesel cost ({partnerVehicleCostsLink} or {regionalVehicleCostsLink})
        </>
      );
    case RegionalOrBusPartnerErrorCode.AdblueCostMissing:
      return (
        <>
          Ad Blue cost ({partnerVehicleCostsLink} or {regionalVehicleCostsLink})
        </>
      );
    case RegionalOrBusPartnerErrorCode.LngCostMissing:
      return (
        <>
          LNG cost ({partnerVehicleCostsLink} or {regionalVehicleCostsLink})
        </>
      );
    case RegionalOrBusPartnerErrorCode.TiresCostMissing:
      return (
        <>
          Cost per Tire ({partnerVehicleCostsLink} or {regionalVehicleCostsLink}
          )
        </>
      );
    case RegionalOrBusPartnerErrorCode.OverheadCostMissing:
      return (
        <>
          Overhead cost ({partnerVehicleCostsLink} or {regionalVehicleCostsLink}
          )
        </>
      );
    case RegionalOrBusPartnerErrorCode.CleaningCostMissing:
      return (
        <>
          Cleaning cost ({partnerVehicleCostsLink} or {regionalVehicleCostsLink}
          )
        </>
      );
    case RegionalOrBusPartnerErrorCode.CountryBusCostEntryMissing:
      return <>All vehicle costs ({regionalVehicleCostsLink})</>;
    case RegionalOrBusPartnerErrorCode.FlixStandardCostMissing:
      return <>Flix standard cost ({regionalVehicleCostsLink})</>;
    case RegionalOrBusPartnerErrorCode.LeasingCostMissing:
      return (
        <>
          Leasing/amortisation cost ({partnerVehicleCostsLink} or{' '}
          {regionalVehicleCostsLink})
        </>
      );
    case RegionalOrBusPartnerErrorCode.InsuranceCostMissing:
      return (
        <>
          Insurance cost ({partnerVehicleCostsLink} or{' '}
          {regionalVehicleCostsLink})
        </>
      );
    case RegionalOrBusPartnerErrorCode.VehicleRegistrationCostMissing:
      return (
        <>
          Vehicle registration cost ({partnerVehicleCostsLink} or{' '}
          {regionalVehicleCostsLink})
        </>
      );
    case RegionalOrBusPartnerErrorCode.VehicleRegistrationCostNotAllowed:
      return (
        <>
          Vehicle registration cost is disabled. Please contact the service desk
        </>
      );
    case RegionalOrBusPartnerErrorCode.MaintenanceCostMissing:
      return (
        <>
          Service & Maintenance cost ({partnerVehicleCostsLink} or{' '}
          {regionalVehicleCostsLink})
        </>
      );
    case RegionalOrBusPartnerErrorCode.MaintenanceCostConflict:
      return (
        <>
          Service & Maintenance cost is defined on both perKm and monthly level
          ({partnerVehicleCostsLink} or {regionalVehicleCostsLink})
        </>
      );
    case RegionalOrBusPartnerErrorCode.ReplacementCostPerKmMissing:
      return (
        <>
          Driver change cost per km ({partnerDriverCostsLink} or{' '}
          {regionalDriverCostsLink})
        </>
      );
    case RegionalOrBusPartnerErrorCode.ReplacementCostPerHrMissing:
      return (
        <>
          Driver change cost per h ({partnerDriverCostsLink} or{' '}
          {regionalDriverCostsLink})
        </>
      );
  }
};

// ---------- Country Rules errors ----------
export enum CountryRulesErrorCode {
  MultiplicationFactorMissing = 'multiplicationFactor.missing',
  DailyAllowanceMissing = 'dailyAllowance.missing',
  SalaryDefinitionsMissing = 'salaryDefinitions.missing'
}

export const isCountryRulesErrorCode = (
  errorCode: string
): errorCode is CountryRulesErrorCode =>
  Object.values(CountryRulesErrorCode).includes(
    errorCode as CountryRulesErrorCode
  );

export const getCountryRulesError = (
  errorCode: CountryRulesErrorCode,
  countryRules: Country,
  year: number
): React.ReactNode => {
  switch (errorCode) {
    case CountryRulesErrorCode.MultiplicationFactorMissing:
      return getRegionalCostsDriverCostsLink(
        countryRules.id,
        year,
        'Multiplication factor'
      );
    case CountryRulesErrorCode.DailyAllowanceMissing:
      return getRegionalCostsDriverCostsLink(
        countryRules.id,
        year,
        'Meals/Daily allowance'
      );
    case CountryRulesErrorCode.SalaryDefinitionsMissing:
      return getRegionalDriverSalaryPeriodsLink(
        countryRules.id,
        year,
        'Driver salary periods'
      );
  }
};

// ---------- Link helpers ----------
const getRegionalCostsVehicleCostsLink = (
  countryId: string,
  year: number,
  label: string
) => (
  <Link
    href={`/${RegionalCostsPath.vehicleCosts}?countryId=${countryId}&year=${year}`}
    target="_blank"
  >
    {label}
  </Link>
);

const getRegionalCostsDriverCostsLink = (
  countryId: string,
  year: number,
  label: string
) => (
  <Link
    href={`/${RegionalCostsPath.driverCosts}?countryId=${countryId}&year=${year}`}
    target="_blank"
  >
    {label}
  </Link>
);

const getRegionalDriverSalaryPeriodsLink = (
  countryId: string,
  year: number,
  label: string
) => (
  <Link
    href={`/${RegionalCostsPath.driverSalaryPeriods}?countryId=${countryId}&year=${year}`}
    target="_blank"
  >
    {label}
  </Link>
);

const getPartnerCostsVehicleCostsLink = (
  busPartnerId: string,
  year: number,
  label: string
) => (
  <Link
    href={`/${PartnerCostsPath.vehicleCosts}?busPartnerId=${busPartnerId}&year=${year}`}
    target="_blank"
  >
    {label}
  </Link>
);

const getPartnerCostsDriverCostsLink = (
  busPartnerId: string,
  year: number,
  label: string
) => (
  <Link
    href={`/${PartnerCostsPath.driverCosts}?busPartnerId=${busPartnerId}&year=${year}`}
    target="_blank"
  >
    {label}
  </Link>
);

export const getVehicleTypeLink = (vehicleTypeId: string, label: string) => (
  <Link
    href={`/${VehicleTypesPath.base}?isPanelOpen=true&vehicleId=${vehicleTypeId}`}
    target="_blank"
  >
    {label}
  </Link>
);
